import { surfaceForSubArea, needsCoverageChoice, coverageOptions, unitsFor, isOfferableOnSurface } from './productCoverage';
/**
 * seedCatalog.js — Product taxonomy and catalog data for the Quote Builder.
 *
 * Hierarchy: Area → Sub-area → Product Category → Product → Square footage
 *
 * Source: SEED_CATALOG.md (originally 8 areas, 20 product categories).
 * OQ-4: Item catalog data source (Fabric vs static) — using static for now.
 *
 * PR-B2b-part-3 (2026-07-25): AREAS moved to constants/vocabSeeds.js as the
 * canonical `definition_home`. This file re-exports the same shape
 * (`{id, label, subAreas: [{id, label}]}`) as a compat shim so existing
 * consumers (AddItemCascade, AccessoryPicker, MasterDefaults, etc.) keep
 * working without a mass-import rewrite. New consumers should read via
 * useSettings.getAreas() to also see tenant-added sub-areas.
 */

import { VOCAB_SEEDS } from '../constants/vocabSeeds';
import { resolveLaborCost } from './resolveLaborCost';

// ─── Areas & Sub-areas (canonical source: vocabSeeds.area.values) ────────────
//
// vocabSeeds stores rows as `{value, label, subAreas: [{value, label}]}` (the
// generic vocab-seed shape). Consumers of this file expect the historical
// `{id, label, subAreas: [{id, label}]}` shape. The map below is the shim.
export const AREAS = (VOCAB_SEEDS.area?.values || []).map((a) => ({
  id: a.value,
  label: a.label,
  subAreas: (a.subAreas || []).map((s) => ({ id: s.value, label: s.label })),
}));

// ─── Product Categories (20) ─────────────────────────────────────────────────

export const PRODUCT_CATEGORIES = [
  { id: 'accessories', label: 'Accessories', marginKey: 'accessories' },
  { id: 'cellulose_blow', label: 'Cellulose Blow', marginKey: 'cellulose' },
  { id: 'cc_spray_foam', label: 'Closed Cell Spray Foam', marginKey: 'spray_foam' },
  { id: 'fg_blow', label: 'Fiberglass Blow', marginKey: 'fiberglass' },
  { id: 'foil_batts', label: 'Foil Batts', marginKey: 'bats' },
  { id: 'fsk_batts', label: 'FSK Batts', marginKey: 'bats' },
  { id: 'hd_fg_board', label: 'HD FG Board', marginKey: 'fiberglass' },
  { id: 'kraft_batts', label: 'Kraft Batts', marginKey: 'bats' },
  { id: 'light_covers', label: 'Light Covers', marginKey: 'accessories' },
  { id: 'mbi_rolls', label: 'MBI Rolls', marginKey: 'fiberglass' },
  { id: 'min_wool_blow', label: 'Min Wool Blow', marginKey: 'fiberglass' },
  { id: 'oc_spray_foam', label: 'Open Cell Spray Foam', marginKey: 'spray_foam' },
  { id: 'polyiso_rigid', label: 'Polyiso Rigid Board', marginKey: 'fiberglass' },
  { id: 'mineral_wool', label: 'Mineral Wool', marginKey: 'bats' },
  { id: 'safb_mineral_wool', label: 'SAFB Mineral Wool', marginKey: 'bats' },
  { id: 'safing', label: 'Safing', marginKey: 'fiberglass' },
  { id: 'unfaced_batts', label: 'Unfaced Batts', marginKey: 'bats' },
  { id: 'xps_foam_board', label: 'XPS Foam Board', marginKey: 'fiberglass' },
  { id: 'aerobarrier', label: 'AeroBarrier', marginKey: 'flow' },
  { id: 'removal', label: 'Removal', marginKey: null },
];

// IDI-75: pricebook.item_type_desc (the legacy ItemType_Desc label, now
// backfilled on ~11,178 base rows — see migration/scripts/idi75-pricebook-item-type-desc.py)
// carries the SAME 15-category vocabulary as the client's audit export, and
// this app's 20 PRODUCT_CATEGORIES already include all 15 plus 5 app-only
// extras (LIGHT COVERS, MBI ROLLS, SAFB MINERAL WOOL, AEROBARRIER, REMOVAL —
// see the audit's "Categories" sheet). This is a label lookup, not a
// classifier: every one of the 20 canonical labels below maps 1:1 to an
// existing PRODUCT_CATEGORIES.id.
const ITEM_TYPE_DESC_TO_CATEGORY_ID = {
  'ACCESSORIES': 'accessories',
  'CELLULOSE BLOW': 'cellulose_blow',
  'CLOSED CELL SPRAY FOAM': 'cc_spray_foam',
  'FIBERGLASS BLOW': 'fg_blow',
  'FOIL BATTS': 'foil_batts',
  'FSK BATTS': 'fsk_batts',
  'HD FG BOARD': 'hd_fg_board',
  'KRAFT BATTS': 'kraft_batts',
  'LIGHT COVERS': 'light_covers',
  'MBI ROLLS': 'mbi_rolls',
  'MIN WOOL BLOW': 'min_wool_blow',
  'OPEN CELL SPRAY FOAM': 'oc_spray_foam',
  'POLYISO RIGID BOARD': 'polyiso_rigid',
  'MINERAL WOOL': 'mineral_wool',
  'SAFB MINERAL WOOL': 'safb_mineral_wool',
  'SAFING': 'safing',
  'UNFACED BATTS': 'unfaced_batts',
  'XPS FOAM BOARD': 'xps_foam_board',
  'AEROBARRIER': 'aerobarrier',
  'REMOVAL': 'removal',
};

/**
 * IDI-75 — resolve a pricebook row's raw item_type_desc label to a
 * canonical PRODUCT_CATEGORIES.id, for rows carrying one of the 20
 * known category labels (case/whitespace-insensitive — the source data
 * carries inconsistent casing and trailing-space variants across 12,290
 * dealer-scoped ItemType rows; see IDI-75 Data Notes).
 *
 * Returns null (NOT a fabricated slug) when the label is empty or doesn't
 * match a known category — callers should fall through to their own
 * tenant-only-category handling (slugifyForCategoryId + the existing
 * resolveTenantCategory/titleCaseCategoryId mechanism) rather than treat
 * null as an error. Measured coverage (2026-08-24, tenant-wide): 9,650 of
 * 11,178 categorized base rows (86%) resolve here; the remainder are
 * genuine per-dealer custom labels ("GUTTERS", "MISC.", etc.) outside the
 * client's 15-category vocabulary.
 *
 * @param {string} label - raw item_type_desc / itemType value
 * @returns {string|null}
 */
export function resolveCategoryIdFromItemTypeDesc(label) {
  if (!label) return null;
  const key = String(label).trim().toUpperCase();
  return ITEM_TYPE_DESC_TO_CATEGORY_ID[key] || null;
}

/**
 * IDI-208 (2026-08-31 ruling) — pattern-match pass for the spray-foam/
 * blown-insulation TAIL: rows whose item_type_desc doesn't match one of the
 * 20 canonical labels verbatim (resolveCategoryIdFromItemTypeDesc returns
 * null) but is clearly a spray-foam or blown-insulation product by free-text
 * description (e.g. "CLOSED CELL SPRAY FOAM - APPROXIMATELY", "INSTALL
 * FIBERGLASS BLOW", "MIN WOOL BLOW"). Without this pass these rows fell
 * through to slugifyForCategoryId and got a fabricated one-off categoryId
 * (e.g. "install_fiberglass_blow") instead of the real canonical category
 * ("fg_blow") — which meant they were never gated by the
 * ItemCoveragePanel coverage-mode requirement real spray/blow products get.
 *
 * Order matters: spray is tested BEFORE blow, because some real labels
 * contain both words ("SPRAY FOAM & FIBERGLASS", a hybrid product listed
 * under its spray-foam line). Cell-type sub-classification (open vs closed)
 * defaults to closed cell when spray-foam-ish wording is present but
 * neither "open" nor "closed" appears — closed cell is the materially more
 * common product in the canonical table (296 rows vs 142 for open, live
 * measurement 2026-08-31).
 *
 * "BIBS" (Blow-In-Blanket System) is checked before the cellulose pattern
 * because it's a trademarked FIBERGLASS product, not cellulose — a plain
 * substring match on "BLOW" alone would have also caught it correctly by
 * accident, but explicitly branching it avoids relying on branch order to
 * get a materially different product family right.
 *
 * "BLOWER DOOR" is explicitly excluded: it's diagnostic test equipment
 * (an airtightness test), not an insulation product, despite containing
 * the substring "BLOW".
 *
 * Verified live (2026-08-31) against every real tail label in
 * bidit-dev-pricebook (active base rows, labels outside the top-30 by
 * frequency): 115 of 118 spray/blow-shaped tail rows classify correctly,
 * the 3 exclusions are all "BLOWER DOOR TEST".
 *
 * @param {string} label
 * @returns {string|null}
 */
export function classifySprayOrBlowFromLabel(label) {
  if (!label) return null;
  const u = String(label).trim().toUpperCase();
  if (/BLOWER\s*DOOR/.test(u)) return null;
  // IDI-208 (Portica #33 catch, delta over develop's shipped pattern-pass):
  // Form factor beats material. A batt is a batt even when it is fiberglass.
  // Without this guard, labels like "KRAFT FACED FIBERGLASS BATTS" would
  // hit the FIBERGLASS material match below and get classified as blown.
  // Returning null lets them slug through slugifyForCategoryId and hit
  // ItemCoveragePanel's cat.includes('batts') check, which gives them the
  // correct single-grid 'rvalue' mode.
  //
  // Population validation status, stated carefully:
  //   * Portica's tenant-270 replay (Portica #33, 2026-08-31, pre-fix on
  //     develop) measured 54 batt rows across 5 labels being misrouted to
  //     blown under the pre-guard classifier.
  //   * Portica's SourceItemList.csv replay (Portica #62, 2026-08-31,
  //     post-fix, 3,886 rows) measured 0 rows changing under this guard —
  //     no fiberglass-batt labels in that source-extract file. This is
  //     not a contradiction: the two populations differ in which labels
  //     they contain. Both readings are correct about their own dataset.
  //   * The guard has NOT been re-replayed against the current live
  //     11,180-row pricebook. Its logic is right, its behaviour on both
  //     replayed populations is either harmless or corrective, but the
  //     "54 rows misrouted" figure describes the pre-guard defect on
  //     tenant 270 specifically, not a general prevalence measurement.
  //
  // BIBS is deliberately excluded — Blown-In Blanket System is a blown
  // product despite the BLANKET suffix. Word boundaries on BATTS/ROLLS
  // ensure the acronym BIBS doesn't accidentally match here.
  if (/\bBATTS?\b|\bROLLS?\b|BLANKET/.test(u)) return null;
  // IDI-208 (Portica #59 + #62 catches, delta over develop's shipped
  // pattern-pass):
  //
  // Original SPRAY match was bare `/SPRAY|SPF\b/`. Portica's #62 replay
  // against SourceItemList.csv (3,886 rows) measured 29 rows changing
  // classification under the tightening — 26 spray adhesives/tips/webs
  // (Item_Type_Desc = ACCESSORIES) that develop was calling spray foam
  // ('3M 74 SPRAY ADHESIVE', 'GORILLA GLUE HEAVY DUTY SPRAY ADHESIVE',
  // etc.) plus 3 damp-spray cellulose rows ('FOREST WOOL 25LB WALL SPRAY
  // CELLULOSE', 'MTNF BLUE WALL SPRAY CELLULOSE 30#', 'APPLEGATE
  // STABILIZED BORA-SPRAY CELLULOSE 30 LB') where the source system's
  // Item_Type_Desc is literally CELLULOSE BLOW.
  //
  // The tightening (SPRAY adjacency to FOAM) is doing all the work on
  // this population; the WALLSPRAY addition to the cellulose branch
  // below is provably inert on SourceItemList (0 labels contain
  // WALLSPRAY as one word). The addition remains load-bearing on the
  // tenant-270 label set specifically, which does carry a literal
  // "WALLSPRAY/DENSEPACK" — see seedCatalog.idi208-classifier.test.js
  // for that fixture case.
  //
  // Test note: seedCatalog.idi208-classifier.test.js previously pinned
  // WALLSPRAY/DENSEPACK to cc_spray_foam with the comment "SPRAY wins
  // here". That pinned outcome was materially wrong and is inverted in
  // this PR.
  if (/SPRAY\s*FOAM|SPF\b/.test(u)) {
    if (/OPEN\s*CELL/.test(u)) return 'oc_spray_foam';
    if (/CLOSED\s*CELL|CLOSE\s*CELL/.test(u)) return 'cc_spray_foam';
    return 'cc_spray_foam';
  }
  if (/\bOPEN\s*CELL\b/.test(u)) return 'oc_spray_foam';
  if (/\bCLOSED\s*CELL\b|\bCLOSE\s*CELL\b/.test(u)) return 'cc_spray_foam';
  if (/\bBIBS\b/.test(u)) return 'fg_blow';
  if (/CELLULOSE|CELLILOSE|NU-WOOL|WALLSPRAY/.test(u)) return 'cellulose_blow';
  if (/MIN(ERAL)?\s*WOOL|ROCKWOOL/.test(u)) return 'min_wool_blow';
  if (/FIBERGLASS|FIBRE\s*GLASS|L77\b/.test(u)) return 'fg_blow';
  if (/BLOW|BLOWN|DENSE\s*PACK|DENSEPACK/.test(u)) return 'fg_blow';
  return null;
}

/**
 * IDI-208 (2026-08-31 ruling, refined per AI code review 2026-08-31) —
 * fallback classification for rows that resolve through NEITHER the
 * canonical table NOR the spray/blow pattern pass above: genuinely
 * unclassifiable dealer-specific labels ("GUTTERS", "MISC.", "JOB
 * SUPPLIES", etc.).
 *
 * Split by whether the label reads as a physical product ("material-like")
 * or a billable activity ("labor-like"): a labor line ("REMOVAL LABOR",
 * "VACUUM SERVICE", "INSTALLATION CHARGE") isn't a catalog item a quote
 * builder droplist should offer alongside real products, so it emits
 * nothing (undefined) rather than a fabricated categoryId — matching the
 * pre-existing "no category" contract for genuinely unlabeled rows. A
 * material-like row with no better category gets bucketed as "accessories"
 * (categoryId 'accessories') rather than a made-up slug, since an
 * unclassified physical product is closer in kind to an accessory
 * (miscellaneous supply item) than to any specific insulation category.
 *
 * REVIEW FIX: the original ruling's regex used a bare `INSTALL` alternative,
 * which matched material-installation lines like "INSTALL - VAPOR BARRIER"
 * or "INSTALL - EAVE VENTS" — confirmed live against bidit-dev-pricebook
 * that these are real rows and are physical-product installs, not labor
 * charges. "INSTALL" alone is now scoped to only match when followed by an
 * explicit labor/billing qualifier (CHARGE, SERVICE, FEE, LABOR) — e.g.
 * "INSTALLATION CHARGE", "INSTALL SERVICE" still correctly classify as
 * labor, but "INSTALL - VAPOR BARRIER" now correctly falls through to the
 * material bucket. Re-verified live: 113 of the original 137 flagged rows
 * still classify as labor-like under the tightened regex; the other 24 are
 * material-installation lines like the ones above, now correctly excluded.
 *
 * @param {string} label
 * @returns {string|undefined} 'accessories' for material-like labels,
 *   undefined (emit nothing) for labor-like labels or an empty label.
 */
export function classifyMaterialOrLaborFallback(label) {
  if (!label) return undefined;
  const LABOR_RE = /REMOV|VACUUM|HAUL|EXTRACT|\bLABOR\b|PREP|FRAMING|DEMO|CLEAN|SERVICE\s*CHARGE|\bCHARGE\b|INSTALL(ATION)?\s*(CHARGE|SERVICE|FEE|LABOR)\b/i;
  if (LABOR_RE.test(label)) {
    return undefined;
  }
  return 'accessories';
}

/**
 * IDI-75 — turn an arbitrary category label into a stable categoryId slug,
 * for the ~14% of categorized rows whose item_type_desc doesn't match one of
 * the 20 canonical labels (dealer-specific categories like "Gutters").
 *
 * Uses the same slugify shape as TCTemplatesPage.js's label->value pattern
 * so the round-trip through titleCaseCategoryId() (which reverses `_` back
 * to spaces + title-cases) reproduces a close approximation of the
 * original label for display, consistent with how the existing
 * tenant-only-category mechanism (resolveTenantCategory) already renders
 * ad-hoc categoryIds elsewhere in the app.
 *
 * @param {string} label
 * @returns {string|null} null when label is empty
 */
export function slugifyForCategoryId(label) {
  if (!label) return null;
  const slug = String(label).trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return slug || null;
}

/**
 * IDI-75 (updated IDI-208, 2026-08-31 ruling) — single entry point: resolve
 * a raw item_type_desc/itemType label to a categoryId. Resolution order:
 *   1. resolveCategoryIdFromItemTypeDesc — exact match against the 20
 *      canonical labels (covers ~87% of active rows).
 *   2. classifySprayOrBlowFromLabel — pattern-match pass for spray-foam/
 *      blown-insulation products described in free text rather than one of
 *      the canonical labels verbatim.
 *   3. classifyMaterialOrLaborFallback — for anything still unresolved:
 *      material-like labels bucket as 'accessories'; labor-like labels
 *      (REMOVAL, INSTALL, SERVICE, etc.) emit undefined rather than a
 *      fabricated category, since a labor line isn't a catalog product.
 *
 * slugifyForCategoryId (below) is NOT called from this entry point as of
 * the 2026-08-31 ruling — classifyMaterialOrLaborFallback now handles
 * every non-empty label that reaches step 3. It remains exported and used
 * standalone by other callers that still want a raw, un-bucketed slug
 * (e.g. a display-only rendering of a tenant-authored category name).
 *
 * This is what QuoteItemsPage.buildCatalogRows/catalogRows.js call instead
 * of leaving categoryId undefined.
 *
 * @param {string} label
 * @returns {string|undefined} undefined when label is empty (preserves the
 *   pre-IDI-75 "no category" contract for genuinely-unlabeled rows, e.g.
 *   the single ItemType_ID 4637 whose source ItemType_Desc is blank), OR
 *   when classifyMaterialOrLaborFallback determines the label is labor-like
 *   (IDI-208: a labor line is not a catalog item).
 */
export function categoryIdFromItemTypeLabel(label) {
  if (!label) return undefined;
  const canonical = resolveCategoryIdFromItemTypeDesc(label);
  if (canonical) return canonical;
  const sprayOrBlow = classifySprayOrBlowFromLabel(label);
  if (sprayOrBlow) return sprayOrBlow;
  // IDI-208: classifyMaterialOrLaborFallback has the FINAL say once we reach
  // here (label is known non-empty at this point) -- its `undefined` return
  // for a labor-like label means "deliberately emit nothing," NOT "try
  // slugifyForCategoryId next." Falling through to slugify on that
  // undefined would defeat the entire point of the labor/material split (a
  // labor line like "REMOVAL LABOR CHARGE" would get a fabricated
  // categoryId and show up in a product droplist as if it were a real,
  // sellable item). slugifyForCategoryId is intentionally NOT called in
  // this branch at all — classifyMaterialOrLaborFallback already covers
  // every non-empty label (material-like -> 'accessories', labor-like ->
  // undefined), so there is no remaining case for slugify to catch.
  return classifyMaterialOrLaborFallback(label);
}

// ─── Products ────────────────────────────────────────────────────────────────
// IDI-136 (Adrian 10-05, "remove the hard-coded placeholder products"): the 25
// built-in sample products are gone. Only the dealer's own Item Catalog lists
// and prices products. A saved line that still stores a built-in productId
// (e.g. "kraft_r13") keeps its stored values (D3); it is never re-priced, and
// buildLineItem refuses it (UNRESOLVABLE_PRODUCT_ID) so an edit must pick a
// catalog row. The retired list lives in quote-builder/__fixtures__/
// sampleProducts.js for tests only. PRODUCTS stays exported (empty) for the
// few readers that still reference it.
export const PRODUCTS = [];

// ─── Lookup Helpers ──────────────────────────────────────────────────────────

/**
 * Get sub-areas for a given area ID.
 * @param {string} areaId
 * @returns {Array<{id: string, label: string}>}
 */
export function getSubAreas(areaId) {
  const area = AREAS.find((a) => a.id === areaId);
  return area?.subAreas || [];
}

/**
 * The DEALER-SET margin for a product category, or null.
 *
 * IDI-309 / IDI-312 (10-02, "no margin a dealer didn't set"): this used to
 * fall back to the hardcoded PRODUCT_CATEGORIES `defaultMargin` (and then to
 * 35%), which priced lines at margins nobody configured and put a category
 * chip on every line. Only the tenant's category_margins count now.
 *
 * @param {string} categoryId
 * @param {object} [tenantCategoryMargins] - Settings -> category_margins
 * @returns {number|null} Margin as decimal (0.35 = 35%), or null
 */
export function getCategoryMargin(categoryId, tenantCategoryMargins = null) {
  const cat = PRODUCT_CATEGORIES.find((c) => c.id === categoryId);
  return dealerCategoryMargin(cat, tenantCategoryMargins);
}

/**
 * IDI-534 (REQ-002): re-derive a LOADED line's categoryMargin from the
 * dealer's Settings. Lines saved before #596 still carry the old seed margin
 * (35% / 50%) the dealer never set; trusting it shows chips nobody set, keeps
 * those lines out of target re-pricing and can make a whole quote read-only.
 * The stored value is ignored: the category's dealer-set margin, or null.
 * Tenant-only and custom categories have no margin key, so they get null.
 *
 * Returns the SAME object when nothing changes, so callers can skip a state
 * update (and the autosave dirty check) on a no-op.
 *
 * @param {object} line
 * @param {object} [tenantCategoryMargins] - Settings -> category_margins
 * @returns {object}
 */
export function rederiveCategoryMargin(line, tenantCategoryMargins = null) {
  if (!line || typeof line !== 'object') return line;
  const cid = line.categoryId ?? line.category;
  const cat = cid == null
    ? null
    : PRODUCT_CATEGORIES.find((c) => String(c.id) === String(cid));
  const next = dealerCategoryMargin(cat, tenantCategoryMargins);
  // The snake_case twin is read as a fallback (`categoryMargin ??
  // category_margin`), so a stale one would resurrect the seed value.
  const hasSnake = Object.prototype.hasOwnProperty.call(line, 'category_margin');
  if (!hasSnake && line.categoryMargin === next) return line;
  const out = { ...line, categoryMargin: next };
  delete out.category_margin;
  return out;
}

/** IDI-534: rederiveCategoryMargin over a list; same array back on a no-op. */
export function rederiveCategoryMargins(lines, tenantCategoryMargins = null) {
  if (!Array.isArray(lines)) return lines;
  let changed = false;
  const out = lines.map((l) => {
    const n = rederiveCategoryMargin(l, tenantCategoryMargins);
    if (n !== l) changed = true;
    return n;
  });
  return changed ? out : lines;
}

function dealerCategoryMargin(category, tenantCategoryMargins) {
  if (!tenantCategoryMargins || !category?.marginKey) return null;
  const tenantVal = tenantCategoryMargins[category.marginKey];
  if (tenantVal == null || tenantVal === '') return null;
  const n = Number(tenantVal);
  // Stored as percentage (e.g. 20) or decimal (e.g. 0.20).
  if (!Number.isFinite(n) || n < 0) return null;
  return n > 1 ? n / 100 : n;
}

/**
 * Build a line item from cascade selections.
 *
 * PR-6: extended to accept per-line extras (`note`, `addlLabor`, `addlCost`,
 * `accessories`, `alternatives`). Previously these were silently dropped by
 * the wizard — they were passed in but the function ignored them.
 *
 * @param {object} params
 * @param {string} params.areaId
 * @param {string} params.subAreaId
 * @param {string} params.categoryId
 * @param {string} params.productId
 * @param {number|string} params.sqft
 * @param {string} [params.note]
 * @param {string|number} [params.addlLabor]
 * @param {string|number} [params.addlCost]
 * @param {Array} [params.accessories]
 * @param {Array} [params.alternatives]
 * @param {string} [params.alias]  PR-62 §3aj: per-line display-name override
 *   for the customer document. Empty/null → not set on the line (falls
 *   through to item_name in QuoteSelectionPanel's label chain).
 * @returns {object} Line item ready for the quote
 */

/**
 * PR-66 s2c helper - synthesize a product-shaped object from a tenant
 * settings.quote_items row so buildLineItem can price it uniformly.
 *
 * Tenant row shape (from QuoteItemsPage / settings.quote_items):
 *   { sku, name, itemType, categoryId, rValue, costCents, isActive, ... }
 *
 * Static PRODUCTS[] shape (what the rest of buildLineItem expects):
 *   { id, categoryId, label, rValue, calcMethod, materialCostPerUnit,
 *     laborCostPerUnit, unit }
 *
 * Field mapping (per Portica 389 ruling + 2b measurement, UPDATED IDI-208/IDI-136):
 *   id                   <- row.sku            (droplists key on sku)
 *   categoryId           <- row.categoryId      (present now: rows arrive via
 *                                                buildCatalogRows, which computes
 *                                                it from item_type_desc)
 *   label                <- row.name
 *   rValue               <- row.rValue || null
 *   calcMethod           <- 'sqft'             (2b: uniform across catalog)
 *   unit                 <- 'sq ft'            (2b: uniform across catalog)
 *   materialCostPerUnit  <- row.itemCostPerUnit (already in cents; the real
 *                                                field name on both
 *                                                buildCatalogRows output AND
 *                                                raw settings.quote_items rows
 *                                                -- NOT costCents, which no
 *                                                writer here ever emits)
 *   laborCostPerUnit     <- null                (see NB below: resolveLaborCost
 *                                                guards on != null, so 0 lies
 *                                                as 'catalog'. null falls through
 *                                                to tier 4 'none' - honest.)
 *
 * Returns null if the input is falsy or not shaped like a tenant row, so
 * callers can chain: `PRODUCTS.find(...) || synthesizeTenantProduct(...)`.
 */
// IDI-499 D2 (Adrian 10-14): AeroBarrier and Removal are "cost per sq ft only;
// the line's sq ft x rate is the price; no chart, no units". The stored cost
// (cost_cents) is cents per sq ft, whatever sq ft per unit says.
export const PER_SQFT_CATEGORY_IDS = ['aerobarrier', 'removal'];
export const isPerSqftCategory = (categoryId) => PER_SQFT_CATEGORY_IDS.includes(categoryId);

function synthesizeTenantProduct(row) {
  if (!row || typeof row !== 'object') return null;
  if (!row.sku) return null;
  return {
    id: row.sku,
    categoryId: row.categoryId || null,
    label: row.name || row.sku,
    // IDI-208 (2026-08-31 audit): carried through so callers can disambiguate
    // duplicate-name collisions in a product droplist (see
    // disambiguateProductLabels below) -- confirmed live against
    // bidit-dev-pricebook that 33.9% of active rows (2,553 of 7,534) share a
    // name with at least one other row (e.g. 30 distinct SKUs are all named
    // literally "Removal"), so `label` alone is not a reliable droplist key
    // for a real tenant's catalog the way it always was for the 25-entry
    // static seed catalog.
    partNumber: row.partNumber || null,
    rValue: row.rValue || null,
    calcMethod: 'sqft',
    unit: 'sq ft',
    // IDI-208/IDI-136: this row is no longer necessarily a raw
    // settings.quote_items overlay entry -- callers (AddItemGroups, via
    // QuoteBuilderPage.loadAccessoryCatalog) now thread the
    // buildCatalogRows(pricebook, quote_items)-MERGED catalog, whose rows
    // carry the real field name `itemCostPerUnit` (already in cents; see
    // QuoteItemsPage/catalogRows.js buildCatalogRows). The old `costCents`
    // read was always a mismatch against the actual quote_items writer
    // shape (which never emitted costCents) and silently priced every
    // tenant/catalog product row at $0 the moment this function actually
    // ran against real data -- see settingsKeyshape.test.js history.
    materialCostPerUnit: Number(row.itemCostPerUnit) || 0,
    // IDI-373: carry the coverage field so buildLineItem can divide cost by
    // coverage instead of multiplying by sqft directly. Without this, a
    // $47.26/bag product × 1000 sqft = $47,260 instead of $613.
    squareFeetPerUnit: Number(row.squareFeetPerUnit || row.sqftPerUnit || row.sqft_per_unit) || 0,
    // IDI-173: the resolved coverage (dealer's own -> IDI central, IDI-338)
    // in the shape quote-builder/productCoverage.js reads for the Coverage
    // dropdown and the line price.
    coverageMode: row.coverage_rvalues_attic || row.coverage_rvalues_wall ? 'rvalue-attic-wall'
      : (Number(row.coverage_per_set) > 0 ? 'sprayfoam' : (row.coverage_rvalues ? 'rvalue' : null)),
    coverageRValuesAttic: row.coverage_rvalues_attic || null,
    coverageRValuesWall: row.coverage_rvalues_wall || null,
    coverageRValues: row.coverage_rvalues || null,
    coveragePerSet: Number(row.coverage_per_set) || null,
    coverageMaxThicknessIn: Number(row.coverage_max_thickness_in) || null,
    // IDI-499 D2: a per-sq-ft product has no chart, so no Coverage dropdown,
    // even when a legacy row still carries one.
    ...(isPerSqftCategory(row.categoryId) ? {
      coverageMode: null, coverageRValuesAttic: null, coverageRValuesWall: null,
      coverageRValues: null, coveragePerSet: null, coverageMaxThicknessIn: null,
    } : {}),
    laborCostPerUnit: null,
    // NB: laborCostPerUnit is null (NOT 0). resolveLaborCost.js:74 guards on
    // `product.laborCostPerUnit != null`; the value 0 satisfies that guard and
    // returns { laborSource: 'catalog' } - a lie, because the catalog never
    // priced this item. `null` falls through to tier 4 ('none'), which is the
    // honest provenance for tenant products with no product-tier labor.
    // Portica ruling 390 §2.
  };
}
export { synthesizeTenantProduct };

/**
 * IDI-208 (2026-08-31 audit finding) — disambiguate a list of
 * droplist-ready products whose `label` collides with a sibling in the SAME
 * list (i.e. within one category's product droplist, the actual scope a
 * dealer sees at once). A raw name collision is common in the real catalog
 * (33.9% of active pricebook rows share a name with at least one other row
 * -- e.g. 30 distinct SKUs are all literally named "Removal") and the
 * droplist rendered ONLY `label` with no way to tell them apart
 * (`AddItemGroups.js` options={availableProducts.map((p) => ({ value: p.id,
 * label: p.label }))}).
 *
 * Appends the part number in parens when it disambiguates (`"Removal
 * (REMOVE-1)"`); falls back to appending the last 6 chars of the SKU when
 * partNumber itself is blank or ALSO collides (rare but possible — SAME
 * partNumber written under two different SKUs). Non-colliding labels are
 * returned completely unchanged, so this is a no-op for the vast majority
 * (66%) of the catalog and for the static seed catalog (no two seed
 * products share a label).
 *
 * @param {Array<{id: string, label: string, partNumber?: string}>} products
 * @returns {Array} same shape, with `label` disambiguated where it collided
 */
export function disambiguateProductLabels(products) {
  if (!Array.isArray(products) || products.length === 0) return products;

  const labelCounts = new Map();
  for (const p of products) {
    if (!p || !p.label) continue;
    labelCounts.set(p.label, (labelCounts.get(p.label) || 0) + 1);
  }

  // Track which DISAMBIGUATED labels have been used so far, in case the
  // partNumber-qualified label itself collides (two rows, same name, same
  // partNumber, different SKU -- confirmed this exists in real data on a
  // small number of rows).
  const usedLabels = new Set();

  return products.map((p) => {
    if (!p || !p.label || (labelCounts.get(p.label) || 0) <= 1) {
      if (p && p.label) usedLabels.add(p.label);
      return p;
    }
    const qualifier = p.partNumber || (p.id ? String(p.id).slice(-6) : '');
    let disambiguated = qualifier ? `${p.label} (${qualifier})` : p.label;
    // Extremely rare second-level collision (same name AND same qualifier) --
    // fall back to the full id so the droplist never silently re-collides.
    if (usedLabels.has(disambiguated) && p.id) {
      disambiguated = `${p.label} (${p.id})`;
    }
    usedLabels.add(disambiguated);
    return { ...p, label: disambiguated };
  });
}

/**
 * PR-66 391 §1 helper - title-case a tenant categoryId for display.
 *
 * Single writer for the tenant-category label rule so the string that ends
 * up on the line's `categoryLabel` field is character-identical to the string
 * the droplist rendered. Previously AddItemGroups computed this inline while
 * buildLineItem fell back to the raw categoryId, so a tenant-only category
 * committed a line whose categoryLabel was `custom_hybrid_foam` while the
 * droplist showed "Custom Hybrid Foam" - that raw string then flowed to the
 * quote document, change orders, and work orders (grep categoryLabel in
 * src/ for the consumer surface).
 *
 * Coerces to String first because a migrated tenant may carry numeric
 * categoryIds; (425).split is not a function. Portica ruling 391 §1.
 */
function titleCaseCategoryId(cid) {
  return String(cid ?? '')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
export { titleCaseCategoryId };

/**
 * IDI-373 (label): the material price PER SQ FT that buildLineItem actually
 * charges for `product`, for display in the item step's cost pill.
 *
 * It follows buildLineItem's material branches EXACTLY, so the label can never
 * disagree with the maths:
 *   - costPerUnit <= 0            -> not priced            -> null
 *   - coverage > 1                -> costPerUnit / coverage (per-unit row: a bag,
 *                                    bundle or set covering `coverage` sq ft)
 *   - anything else               -> not priceable by area  -> null
 *
 * Returns { perSqftCents, perUnitCents, coverage } or null. perSqftCents is
 * UNROUNDED (cents, may be fractional). Round only when displaying.
 *
 * Before this the pill printed the raw per-UNIT cost with "/sq ft": a Kraft
 * batt at $40.11 per 148.54 sq ft bundle read "$40.11/sq ft" (true: $0.27).
 */
export function materialPricePerSqft(product, coverageSqftPerUnit = null) {
  if (!product) return null;
  const costPerUnit = Number(product.materialCostPerUnit) || 0;
  if (costPerUnit <= 0) return null;
  if (isPerSqftCategory(product.categoryId)) {
    return { perSqftCents: costPerUnit, perUnitCents: costPerUnit, coverage: null };
  }
  // IDI-173: a chart product prices with the CHOSEN coverage entry (the
  // Coverage dropdown); its own sq ft per unit is a degenerate 1.
  const coverage = Number(coverageSqftPerUnit) > 0 ? Number(coverageSqftPerUnit) : (Number(product.squareFeetPerUnit) || 0);
  if (coverage > 1) {
    return { perSqftCents: costPerUnit / coverage, perUnitCents: costPerUnit, coverage };
  }
  // IDI-136: no built-in per-sq-ft seed rate any more.
  return null;
}

/**
 * PR-66 391 §1 helper - resolve a tenant-only categoryId to a
 * category-shaped object for buildLineItem's `category` lookup.
 *
 * Returns null when:
 *   - `tenantProducts` is not an array (no tenant catalog was threaded), or
 *   - no tenant row references this categoryId (the categoryId is truly
 *     unknown and buildLineItem's final `|| categoryId` fallback is the
 *     correct behavior - write the raw string, don't fabricate a label).
 *
 * Returns a category-shaped object when at least one tenant row references
 * the categoryId. Shape matches PRODUCT_CATEGORIES entries so downstream code
 * (categoryMargin cascade, categoryLabel) works uniformly. `defaultMargin`
 * and `marginKey` are null - tenant-only categories fall through to the
 * customer/quote margin per §3ao, and have no configurable lever until
 * 382 §2.1 / PR-69 lands.
 */
function resolveTenantCategory(categoryId, tenantProducts) {
  if (categoryId == null) return null;
  if (!Array.isArray(tenantProducts)) return null;
  const cidStr = String(categoryId);
  const hasReferent = tenantProducts.some(
    (r) => r && r.categoryId != null && String(r.categoryId) === cidStr
  );
  if (!hasReferent) return null;
  return {
    id: categoryId,
    label: titleCaseCategoryId(categoryId),
    defaultMargin: null,
    marginKey: null,
  };
}
export { resolveTenantCategory };

export function buildLineItem({
  areaId,
  subAreaId,
  // PR-67a hotfix (2026-08-22): optional pre-resolved labels. Callers that
  // hold the merged area/sub-area labels (e.g. AddItemCascade with its
  // `mergedAreasForCascade` + `mergedSubAreasForArea` memos) pass them in
  // so the committed line stamps the TENANT's label, not the VOCAB_SEEDS
  // default. Absent → fall back to the static AREAS lookup below
  // (preserves the pre-hotfix contract for migrated callers + existing
  // tests). This closes the enabler-without-consumer gap: PR-67a shipped
  // the merge overlay but committed lines still stamped from AREAS →
  // renamed areas rendered as their default label; renamed sub-areas
  // rendered as a raw UUID (subArea.id fallback). See red-proofs A/B in
  // the PR body.
  //
  // Fallback order (`??` cascade):
  //   caller-supplied label → AREAS lookup label → id → null (sub only).
  areaLabel: areaLabelArg,
  subAreaLabel: subAreaLabelArg,
  categoryId,
  productId,
  sqft,
  // PR-6: per-line extras threaded through so they survive commit.
  note = '',
  addlLabor = '',
  addlCost = '',
  accessories = [],
  alternatives = [],
  // PR-62 §3aj: per-line display-name override for the customer document.
  // See quote-builder/optionLabel.js VOCAB block. `alias` is the canonical
  // key (camelCase, N-14). Empty string → line has no alias field written.
  alias = '',
  // §4b: tenant's category_margins from Settings. When provided, the line's
  // categoryMargin is looked up from here (via the category's marginKey)
  // (IDI-309: there is no hardcoded fallback any more). This is what makes the
  // Margins & Labor settings page functional.
  tenantCategoryMargins = null,
  // B5 (Iter-20): sub-area labor config and tenant defaults for the
  // precedence chain (sub-area override > area default > product catalog).
  subAreaLaborConfig = null, // { laborMethod, laborRate, laborUnit, inheritsDefault }
  tenantLaborDefaults = null, // { per_sq_ft, per_linear_ft, hourly, day }
  // PR-66 s2c: optional tenant Quote Items catalog (settings.quote_items rows
  // filtered to insulation itemTypes). When provided and the static PRODUCTS[]
  // lookup misses, this list is tried as a fallback. A tenant row shape is
  //   { sku, name, itemType, categoryId, rValue, costCents, ... }
  // and gets synthesized into the product shape buildLineItem expects. Per 2b
  // measurement (all 25 static insulation products use calcMethod:'sqft',
  // unit:'sq ft'), the synthesis sets calcMethod:'sqft', unit:'sq ft'.
  // laborCostPerUnit is set to null so the labor tier chain (sub-area > area >
  // product-tier) falls through PAST the product tier to 'none' when neither
  // sub-area nor area defaults are configured. Portica ruling 390 §2: 0 lies
  // as laborSource='catalog' because resolveLaborCost guards on != null;
  // null is honest ('catalog never priced this') and preserves the guard for
  // static products with a legitimate 0 rate.
  tenantProducts = null,
  // IDI-173: the Coverage dropdown choice ("R-30" or "3in") and the sub-area's
  // surface ("attic" | "wall"). Absent for products with no chart.
  coverageChoice = null,
  coverageSurface = null,
}) {
  // Portica ruling 390 §7: coerce both sides to String at lookup. Migrated
  // tenants (Kyle) can carry numeric SKUs; a persisted productId serialized as
  // a JSON number won't strict-equal a string sku. Coercion at lookup is
  // cheaper (and safer) than coercion at every writer, and it doesn't affect
  // resolution when both sides are already strings.
  // IDI-136: only the dealer's catalog resolves a product (no built-ins).
  const product =
    (Array.isArray(tenantProducts)
      ? synthesizeTenantProduct(
          tenantProducts.find((p) => p && String(p.sku) === String(productId))
        )
      : null);
  // Portica ruling 391 §1: category lookup is BOTH string-coerced AND
  // tenant-aware. The prior fix pack widened `product` for tenant SKUs and
  // left `category` static-only + strict `===` — exactly the twin uncoerced
  // and un-widened identifier that ships a raw `custom_hybrid_foam` string as
  // the line's `categoryLabel` when a tenant creates a new categoryId. That
  // string flows to EditItemModal, CommittedLineCard, ChangeOrder engine,
  // woMaterialsProjection — customer-facing surfaces.
  // Portica ruling 391 §1: category lookup is BOTH string-coerced AND
  // tenant-aware. The prior fix pack widened `product` for tenant SKUs and
  // left `category` static-only + strict `===` — exactly the twin uncoerced
  // and un-widened identifier that ships a raw `custom_hybrid_foam` string as
  // the line's `categoryLabel` when a tenant creates a new categoryId. That
  // string flows to EditItemModal, CommittedLineCard, ChangeOrder engine,
  // woMaterialsProjection — customer-facing surfaces.
  const category = PRODUCT_CATEGORIES.find((c) => String(c.id) === String(categoryId))
    || resolveTenantCategory(categoryId, tenantProducts);
  const area = AREAS.find((a) => a.id === areaId);
  const subArea = area?.subAreas.find((s) => s.id === subAreaId);

  // PR-66 §2a: refuse to price an unresolvable productId.
  //
  // Previously, if `product` was undefined (productId not in the static
  // PRODUCTS[] catalog), buildLineItem silently produced a $0 line:
  //   materialCost   = 0            (product ? ... : 0 at the assign below)
  //   laborCost      = 0            (product-tier fallback breaks with no product)
  //   calcMethod     = 'sqft'       (default)
  //   unit           = 'sq ft'      (default)
  //   productLabel   = productId    (the raw sku, not a human name)
  //   rValue         = null
  //
  // That is a wire lie (money defect: silent $0 line). We refuse instead,
  // and let the caller surface the error to the operator. When PR-66 §2c
  // lands, this guard will also try a tenant-side product lookup before
  // throwing — but the refusal semantics stay the same for the residual
  // "neither static nor tenant catalog can resolve this productId" case.
  if (productId && !product) {
    const err = new Error(
      `buildLineItem: productId "${productId}" not found in catalog. Refresh Quote Items or contact support.`
    );
    err.code = 'UNRESOLVABLE_PRODUCT_ID';
    err.productId = productId;
    throw err;
  }

  // B5 (Iter-20): labor cost resolved via precedence chain.
  // Tier 1: sub-area override > Tier 2: area default > Tier 3: product catalog.
  // Resolve the area default from the flat labor_rates store.
  // LAB-A: resolve the area default from labor_rates.
  // The product's calcMethod determines which piece rate to use.
  // Supports both canonical shape (piece: { sq_ft, linear_ft }) and legacy (per_sq_ft).
  const calcMethod = product?.calcMethod || 'sqft';
  const areaDefault = tenantLaborDefaults ? (() => {
    const isLinear = calcMethod === 'linear_ft' || calcMethod === 'linearFt';
    // Canonical shape: piece.sq_ft / piece.linear_ft
    if (tenantLaborDefaults.piece && typeof tenantLaborDefaults.piece === 'object') {
      const rate = isLinear
        ? Number(tenantLaborDefaults.piece.linear_ft) || 0
        : Number(tenantLaborDefaults.piece.sq_ft) || 0;
      return { rate, rateType: 'piece' };
    }
    // Legacy shape fallback: per_sq_ft / per_linear_ft
    const rate = isLinear
      ? Number(tenantLaborDefaults.per_linear_ft) || 0
      : Number(tenantLaborDefaults.per_sq_ft) || 0;
    return { rate, rateType: 'piece' };
  })() : null;
  const { laborCost, laborSource } = resolveLaborCost({
    quantity: sqft,
    subArea: subAreaLaborConfig,
    areaDefault,
    product,
  });
  // IDI-373: material cost (Adrian ruling 2026-09-24, superseding the
  // 3-tier precedence of 09-15).
  //
  // Coverage is REQUIRED to price an area-quoted product:
  //   cost = (sqft / sqftPerUnit) * costPerUnit
  //   e.g. 1000 sqft / 77 sqft-per-bag * $47.26/bag = $613.77
  //
  // The old `else` branch treated costPerUnit as if it were already per-sqft
  // and returned `costPerUnit * sqft`. That is the defect: it turned a $47.26
  // bag into $47,260 for 1,000 sqft. There is no way to tell a per-bag price
  // from a per-sqft price without coverage, so guessing is never right --
  // products that cannot be priced this way are now HIDDEN from the
  // area-priced picker (see catalogRows.hasUsableCoverage) instead of being
  // silently mispriced here.
  //
  // Gate is `> 1`, not `> 0`: 645 migrated rows carry a degenerate coverage of
  // exactly 1, which would reproduce the per-sqft bug precisely.
  //
  // IDI-136: the built-in per-sqft seed exemption is gone with the built-ins.
  let materialCost = 0;
  let lineCoverage = null;
  if (product) {
    const costPerUnit = Number(product.materialCostPerUnit) || 0;
    // IDI-173: a chart product prices with the CHOSEN coverage entry; the
    // product's own sq ft per unit is only used when there's no chart.
    const surfaceForLine = coverageSurface || surfaceForSubArea(areaId, subAreaLabelArg);
    let coverage = Number(product.squareFeetPerUnit) || 0;
    // IDI-434 (ruled): a chart product with no chart for this surface is not
    // offered here. Refuse, never price it from the other surface's chart or
    // its generic sq ft per unit.
    if (!isOfferableOnSurface(product, surfaceForLine)) {
      const err = new Error(`This product has no ${surfaceForLine} coverage chart.`);
      err.code = 'COVERAGE_NOT_FOR_SURFACE';
      throw err;
    }
    if (needsCoverageChoice(product, surfaceForLine)) {
      const opts = coverageOptions(product, surfaceForLine);
      // IDI-173: with exactly one coverage entry there is nothing to choose;
      // use it (a one-row chart shouldn't block the line). With several and
      // no choice, the line is unpriceable: refuse, like an unknown product,
      // rather than silently price it at $0.
      const opt = opts.find((o) => o.value === coverageChoice) || (opts.length === 1 ? opts[0] : null);
      if (!opt) {
        const err = new Error('Choose a coverage for this product before adding it.');
        err.code = 'COVERAGE_REQUIRED';
        throw err;
      }
      lineCoverage = { ...opt, surface: surfaceForLine };
      coverage = opt.sqftPerUnit;
    }
    // IDI-373 (ruling widened 2026-09-24): a dealer price of $0 means NOT SET,
    // not free. cost_cents is 0 on 99.6% of catalog rows because price lives on
    // dealer-override rows; treating 0 as a real price yields a $0 line, which
    // is the same class of wrong answer as the $47,260 bag. Leave 0 and let the
    // picker filter keep these rows out of reach.
    if (costPerUnit <= 0) {
      materialCost = 0;
    } else if (isPerSqftCategory(product.categoryId)) {
      materialCost = Math.round(costPerUnit * sqft);
    } else if (coverage > 1) {
      materialCost = Math.round((sqft / coverage) * costPerUnit);
    } else {
      // Tenant/migrated row with no usable coverage: unpriceable by area.
      // Should be unreachable from the picker; 0 rather than a wrong number.
      materialCost = 0;
    }
  }

  return {
    id: `li-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    areaId,
    // PR-67a hotfix: caller-supplied label wins; AREAS lookup is the
    // fallback (preserves pre-hotfix behavior for callers that don't pass).
    //
    // Cascade asymmetry is DELIBERATE (do not "harmonise" the two lines):
    //   • area cascade terminates at `areaId` — a line always has an id.
    //   • subArea cascade terminates at `null` — a line legitimately has
    //     no sub-area (Options / non-sub-divided areas). `null` is the
    //     well-formed sentinel; `undefined` would leak past this line and
    //     downstream code doesn't discriminate "no sub-area" from "couldn't
    //     resolve."
    //
    // Operator is `??` (nullish), NOT `||` (falsy). A caller passing `''`
    // MUST fall through to the AREAS lookup, not stamp an empty label.
    // See the `?? nullish semantics` test in seedCatalog.test.js; do not
    // "simplify" to `||` — that receipt exists for exactly this reason.
    areaLabel: areaLabelArg ?? area?.label ?? areaId,
    subAreaId: subAreaId || null,
    subAreaLabel: subAreaLabelArg ?? subArea?.label ?? subAreaId ?? null,
    categoryId,
    categoryLabel: category?.label || categoryId,
    productId,
    productLabel: product?.label || productId,
    // IDI-173: a chart product's R-value is the one the user CHOSE, not the
    // product's single (often empty) rValue. The line stores the choice, the
    // surface and the sq ft per unit it priced with, plus the unit count
    // (bags / sets) that the Material List and work order read (IDI-430 #3).
    rValue: lineCoverage ? (lineCoverage.rValue || product?.rValue || null) : (product?.rValue || null),
    ...(lineCoverage ? {
      coverageChoice: lineCoverage.value,
      coverageSurface: lineCoverage.surface,
      coverageSqftPerUnit: lineCoverage.sqftPerUnit,
      coverageUnit: lineCoverage.unitLabel,
      bidItemUsage: unitsFor(sqft, lineCoverage.sqftPerUnit, lineCoverage.unitLabel),
    } : {}),
    calcMethod: product?.calcMethod || 'sqft',
    sqft,
    // PR-6: unit follows catalog (Q1). Field name stays `sqft`; the display
    // label is catalog-driven via getMeasurementLabel(). Fallback = 'sq ft'.
    unit: product?.unit || 'sq ft',
    materialCost: materialCost,
    laborCost: laborCost,
    laborSource: laborSource, // B5: which tier produced this number
    otherCost: 0,
    // §4b: the tenant's category_margins (Settings -> Margins & Labor) ONLY.
    // IDI-309 / IDI-312: the hardcoded seed `defaultMargin` is no longer a
    // margin source. A category the dealer hasn't priced has no category
    // margin, so the line cascades to customer / quote margin and shows no
    // category chip.
    categoryMargin: dealerCategoryMargin(category, tenantCategoryMargins),
    // IDI-95: whether this line's category actually maps to one of the
    // Margins & Labor buckets. `category` can be non-null but STILL have
    // no bucket (marginKey===null -- true for 'removal' AND for every
    // tenant-only category from resolveTenantCategory, which explicitly
    // sets marginKey:null per Portica ruling 382 §2.1/391 §1 -- there is no
    // configurable lever for those yet). Previously this fell through to
    // categoryMargin=null -> quote/customer default SILENTLY -- no signal
    // anywhere that the item's actual category was never priced by the
    // tenant's own Margins & Labor settings. CommittedLineCard surfaces
    // this as a visible badge instead.
    hasCategoryMarginBucket: Boolean(category?.marginKey),
    lineMargin: null,    // No override yet
    lineTotal: null,     // Calculated by margin engine
    isCustom: false,     // M-6: catalog item = read-only cost
    // PR-6: per-line extras (previously silently dropped by the old wizard).
    notes: note || '',
    additionalLabor: addlLabor === '' || addlLabor == null ? null : Number(addlLabor),
    additionalCost: addlCost === '' || addlCost == null ? null : Number(addlCost),
    accessories: Array.isArray(accessories) ? accessories : [],
    alternates: Array.isArray(alternatives) ? alternatives : [],
    // PR-62 §3aj: alias only appears on the object when non-empty. Absent
    // alias means "no override" and QuoteSelectionPanel's label chain falls
    // through to item_name. Omitting the key is cleaner than persisting ''.
    ...(alias && alias.trim() ? { alias: alias.trim() } : {}),
  };
}

/**
 * getMeasurementLabel — human-readable label for the measurement input field.
 * Follows catalog `unit`. Fallback: 'Sq ft' (title-case for input labels).
 *
 * PR-6: used by AddItemGroups active-draft measurement input so Insulation
 * shows "Sq ft" and Air Sealing shows "Lin ft" (whenever a product's unit
 * is set to 'lin ft' in the catalog).
 */
export function getMeasurementLabel(product) {
  const unit = product?.unit || 'sq ft';
  // Title-case first letter: 'sq ft' → 'Sq ft', 'lin ft' → 'Lin ft'.
  return unit.charAt(0).toUpperCase() + unit.slice(1);
}

/**
 * Build a custom line item (M-6: editable cost).
 *
 * PR-67a hotfix (2026-08-22): accepts optional `areaLabel` / `subAreaLabel`
 * for the same reason as buildLineItem — twin (§1.17 pattern). Fixing
 * buildLineItem alone and leaving this stamping from static AREAS would
 * ship the exact bug PR-67a-hotfix exists to fix, on the custom-item flow.
 */
export function buildCustomLineItem({
  areaId,
  subAreaId,
  areaLabel: areaLabelArg,
  subAreaLabel: subAreaLabelArg,
  description,
  materialCost,
  laborCost,
  margin,
  alias = '',
}) {
  const area = AREAS.find((a) => a.id === areaId);
  const subArea = area?.subAreas.find((s) => s.id === subAreaId);

  return {
    id: `li-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    areaId,
    // PR-67a hotfix twin (§1.17). Same cascade shape + same asymmetry as
    // buildLineItem — see the receipt at the buildLineItem return block
    // above. Keeping the two lines byte-identical is the point: fixing one
    // and not the twin is the §1.17 bug this hotfix specifically avoids.
    areaLabel: areaLabelArg ?? area?.label ?? areaId,
    subAreaId: subAreaId || null,
    subAreaLabel: subAreaLabelArg ?? subArea?.label ?? subAreaId ?? null,
    categoryId: 'custom',
    categoryLabel: 'Custom',
    productId: null,
    productLabel: description || 'Custom Item',
    rValue: null,
    calcMethod: 'manual',
    sqft: null,
    unit: null,
    // PR-06 FIX (2026-07-22): operator ruling — custom items may be created
    // WITHOUT a quantity. UI must surface a "Set quantity" affordance later.
    // We deliberately do NOT default to 1 (that would be "inventing" a value).
    quantity: null,
    materialCost: materialCost || 0,
    laborCost: laborCost || 0,
    otherCost: 0,
    categoryMargin: null,
    lineMargin: margin ?? null,
    lineTotal: null,
    isCustom: true,      // M-6: custom item = editable cost
    notes: '',
    alternates: [],
    // PR-62 §3aj: alias only appears on the object when non-empty.
    ...(alias && alias.trim() ? { alias: alias.trim() } : {}),
  };
}
